/**
 * Scroll-snap based carousel controller.
 *
 * Expected markup:
 * - One element with [data-carousel-track]
 * - One or more items with [data-carousel-item] inside the track
 * - Optional previous/next buttons with [data-scroll-dir="prev|next"]
 *
 * The component keeps button disabled states in sync with the current
 * horizontal scroll position.
 *
 * @extends HTMLElement
 */
class PDScrollCarousel extends HTMLElement {
  /**
   * Initializes element references and event listeners when the component
   * is connected to the DOM.
   *
   * @returns {void}
   */
  connectedCallback() {
    /** @type {HTMLElement | null} */
    this.track = this.querySelector("[data-carousel-track]");

    /** @type {HTMLButtonElement[]} */
    this.buttons = Array.from(this.querySelectorAll("[data-scroll-dir]"));

    if (!this.track || this.buttons.length === 0) return;

    this.buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const direction = button.dataset.scrollDir === "prev" ? -1 : 1;
        this.scrollByItem(direction);
      });
    });

    this.track.addEventListener("scroll", () => {
      window.requestAnimationFrame(() => this.updateButtons());
    });

    window.addEventListener("resize", () => this.updateButtons());
    this.updateButtons();
  }

  /**
   * Scrolls the track by one item width plus the track gap.
   *
   * @param {number} direction Scroll direction: -1 for previous, 1 for next.
   * @returns {void}
   */
  scrollByItem(direction) {
    const item = this.track.querySelector("[data-carousel-item]");
    const gap = parseFloat(window.getComputedStyle(this.track).columnGap || 0);
    const delta = item
      ? item.getBoundingClientRect().width + gap
      : this.track.clientWidth * 0.9;

    this.track.scrollBy({
      left: delta * direction,
      behavior: "smooth",
    });
  }

  /**
   * Updates previous/next button disabled states based on current scroll
   * boundaries.
   *
   * @returns {void}
   */
  updateButtons() {
    if (!this.track) return;

    const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 4;
    const atEnd = this.track.scrollLeft >= maxScrollLeft - 4;

    this.buttons.forEach((button) => {
      const isPrev = button.dataset.scrollDir === "prev";
      button.disabled = isPrev ? atStart : atEnd;
    });
  }
}

if (!customElements.get("pd-scroll-carousel")) {
  customElements.define("pd-scroll-carousel", PDScrollCarousel);
}
